import { tryGetShortcutKey } from 'components/WorkspaceEditor/utils/shortcutKeyUtils'
import { PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING } from 'pages/ProjectsPage/WorkspacePage/components/constants'

const {
    Enter,
    Escape,
    ArrowDown,
    ArrowUp,
} = PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING

/**
 * Checks if no modifier keys (Ctrl, Shift, Alt, Meta) are pressed.
 * @param event - The keyboard event to check
 * @returns True if no modifier keys are pressed, false otherwise
 */
const checkIfNoModifierKeysPressed = (
    event: React.KeyboardEvent<HTMLElement>
): boolean => {
    return !event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey
}

/**
 * Checks if the Enter key operation is triggered by a keyboard shortcut.
 *
 * @param event - The keyboard event to check
 * @returns True if the Enter key operation is triggered, false otherwise
 */
export const isEnterShortcut = (
    event: React.KeyboardEvent<HTMLElement>
): boolean => {
    if (tryGetShortcutKey(Enter, event)) {
        event.preventDefault() // Prevent default browser behavior
        return true
    }
    return false
}

/**
 * Checks if the CTRL+S key operation is triggered by a save key shortcut.
 *
 * @param event - The keyboard event to check
 * @returns True if the CTRL+S key operation is triggered, false otherwise
 */
export const isSaveKeyShortcut = (
    event: React.KeyboardEvent<HTMLElement>
): boolean => {
    // Check for Ctrl+S or Cmd+S
    if (tryGetShortcutKey('Save', event)) {
        event.stopPropagation() // Important: Stop it from bubbling
        event.preventDefault() // Prevent the browser's save dialog
        return true
    }
    return false
}

/**
 * Checks if the Escape key operation is triggered by a keyboard shortcut.
 *
 * @param event - The keyboard event to check
 * @returns True if the Escape key operation is triggered, false otherwise
 */
export const isEscapeKeyShortcut = (
    event: React.KeyboardEvent<HTMLElement>
): boolean => {
    // Check for Escape key
    if (tryGetShortcutKey(Escape, event)) {
        event.preventDefault() // Prevent default browser behavior
        return true
    }
    return false
}

/**
 * Checks if the Arrow Up key is pressed for navigation without modifiers.
 *
 * @param event - The keyboard event to check
 * @returns True if Arrow Up key is pressed without modifiers, false otherwise
 */
export const isArrowUpShortcut = (
    event: React.KeyboardEvent<HTMLElement>
): boolean => {
    const isNoModifierKeyPressed = checkIfNoModifierKeysPressed(event)

    // Check for Arrow Up key
    if (isNoModifierKeyPressed && tryGetShortcutKey(ArrowUp, event)) {
        event.preventDefault() // Prevent default browser behavior
        return true
    }
    return false
}

/**
 * Checks if the Arrow Down key is pressed for navigation without modifiers.
 *
 * @param event - The keyboard event to check
 * @returns True if Arrow Down key is pressed without modifiers, false otherwise
 */
export const isArrowDownShortcut = (
    event: React.KeyboardEvent<HTMLElement>
): boolean => {
    const isNoModifierKeyPressed = checkIfNoModifierKeysPressed(event)

    // Check for Arrow Down key
    if (isNoModifierKeyPressed && tryGetShortcutKey(ArrowDown, event)) {
        event.preventDefault() // Prevent default browser behavior
        return true
    }
    return false
}

/**
 * Determines if a keyboard event should be handled based on event type and key.
 * Uses onKeyUp for Escape (more reliable with CKEditor) and onKeyDown for all other keys.
 *
 * @param event - The keyboard event to check
 * @param eventType - Whether this is a 'up' or 'down' event
 * @returns True if the event should be handled, false otherwise
 */
export const shouldHandleKeyEvent = (
    event: React.KeyboardEvent<HTMLElement>,
    eventType: 'up' | 'down'
): boolean => {
    const isEscapeKey = isEscapeKeyShortcut(event)

    // Use onKeyUp for Escape (more reliable with CKEditor)
    // Use onKeyDown for all other keys (immediate response needed)
    return eventType === 'up' ? isEscapeKey : eventType === 'down'
}